/**
 * Agent Admin Service (spec 014 — Independent Business Agent, T118)
 *
 * API client for the IBA admin approval + audit endpoints hosted on
 * ServiceManager.API (specs/014-independent-business-agent/contracts/agent-api.yaml):
 *
 *   GET  /api/agents/admin/pending?status=      — agent applications by status
 *   GET  /api/agents/admin/{spid}               — detail: earnings ledger + lifecycle history
 *   POST /api/agents/admin/{spid}/approve       — approve; unlocks Locked earnings transactionally
 *   POST /api/agents/admin/{spid}/reject        — reject; earnings stay Locked
 *   POST /api/agents/admin/{spid}/suspend       — suspend an approved agent (deactivates referral code)
 *   GET  /api/agents/admin/earnings/held        — earnings held by a cap (US-044-06)
 *   POST /api/agents/admin/earnings/{id}/release|reverse — resolve a held row; `{ reason }` required
 *   POST /api/agents/admin/{spid}/earnings/rerun-all     — re-run accrual; no body
 *   POST /api/agents/admin/{spid}/link/clear    — clear the linked Chelless account; `{ reason }` required
 *
 * The action endpoints accept an optional JSON body `{ reason }` which is
 * recorded in the agent's audit history; an empty body is also allowed.
 *
 * Unlike the TaskManager-backed admin services (relative /api paths through the
 * Vite proxy / gateway), these endpoints live on ServiceManager.API — a different
 * host. The base URL is injected at build time via VITE_SERVICEMANAGER_API_URL;
 * when unset (local dev) paths stay relative and the Vite /api/agents proxy
 * (vite.config.ts) routes them.
 *
 * Uses the established authenticatedFetch pattern from src/api/ for automatic
 * Bearer token injection. Endpoints accept the Admin, SystemAdmin, or
 * PlatformAdmin role claim (AgentController inline admin gate — "Admin" is the
 * legacy claim the existing admin account carries, matching this app's
 * AuthContext gate). Non-admin tokens yield 401/403.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

// ---------------------------------------------------------------------------
// Base URL — ServiceManager.API host (build-time), relative in local dev
// ---------------------------------------------------------------------------

const SERVICEMANAGER_BASE: string = import.meta.env.VITE_SERVICEMANAGER_API_URL || '';

const AGENTS_ADMIN = `${SERVICEMANAGER_BASE}/api/agents/admin`;

// ---------------------------------------------------------------------------
// Response shapes — aligned to the serialized AgentController admin DTO
// (System.Text.Json camelCase; confirmed with the API implementation)
// ---------------------------------------------------------------------------

export type AgentApprovalStatus = 'Pending' | 'Approved' | 'Rejected' | 'Suspended';

/**
 * One agent application row from GET /api/agents/admin/pending.
 * AgentProfile (agent-api.yaml) extended with lockedUsd + mainAppProfileId.
 * referralCode/qrPayloadUrl can be null — code minting degrades gracefully
 * when the TaskManager mint endpoint is unavailable.
 */
export interface AgentAdminRow {
  spid: number;
  displayName: string;
  referralCode: string | null;
  qrPayloadUrl: string | null;
  approvalStatus: AgentApprovalStatus;
  mainAppProfileId: string | null;
  /** Sum (USD) of this agent's Status=Locked earnings ledger rows; 0 when none. */
  lockedUsd: number;
  /** ISO 8601 timestamp of the application (IndependentAgent row creation). */
  createdAt: string;
}

/** One lifecycle-history entry from the agent audit trail. */
export interface AgentAuditEntry {
  id: number;
  action: string;
  fromStatus: AgentApprovalStatus | null;
  toStatus: AgentApprovalStatus | null;
  actor: string | null;
  reason: string | null;
  createdAt: string;
}

/**
 * ServiceManager sends AgentEarningType by NAME (global JsonStringEnumConverter);
 * the numeric codes 1-5 stay accepted.
 */
export type AgentEarningTypeName =
  | 'AppReferralActivation'
  | 'AppReferralUpgrade'
  | 'ServiceLeadFee'
  | 'AgentRecruitment'
  | 'RecruiterOverride';
export type AgentEarningType = AgentEarningTypeName | 1 | 2 | 3 | 4 | 5;

/** One earnings-ledger row in the admin detail view. */
export interface AgentEarningsEntry {
  id: number;
  earningType: AgentEarningType;
  sourceReference: string;
  amountUsd: number;
  status: string;
  description: string | null;
  createdAt: string;
  /** Reversed earning whose recruiter override was never clawed back; reversing again retries the cascade. */
  hasStandingOverride?: boolean;
}

/**
 * One direct recruit in the agent's downline (umbrella team). bountyUsd /
 * overrideUsd are what the VIEWED agent earned from this recruit — the
 * recruitment bounty and the net override commission in USD.
 */
export interface AgentDownlineEntry {
  spid: number;
  displayName: string;
  approvalStatus: AgentApprovalStatus;
  /** ISO 8601 timestamp the recruit joined (their IndependentAgent row creation). */
  joinedAt: string;
  /** Recruitment bounty (USD) the viewed agent earned from this recruit. */
  bountyUsd: number;
  /** Net override commission (USD) the viewed agent earned from this recruit. */
  overrideUsd: number;
  /** Not yet computed by the API — always null today. */
  referredUserCount: number | null;
  /** Not yet computed by the API — always null today. */
  activatedReferralCount: number | null;
}

export type AgentInviteKind = 'EmailInvite' | 'DirectRegistration';
export type AgentInviteStatus = 'Sent' | 'Claimed' | 'Expired' | 'Revoked';

/**
 * One recruiting invite issued by the agent. The admin surface intentionally
 * omits claimUrl — the claim link is never exposed here.
 */
export interface AgentInviteEntry {
  id: number;
  email: string | null;
  displayName: string | null;
  kind: AgentInviteKind;
  status: AgentInviteStatus;
  /** SPID of the agent created when the invite was claimed, else null. */
  claimedSpid: number | null;
  expiresAt: string | null;
  createdAt: string;
}

/** An explicit write-off of a clawback shortfall that could not be recovered from the agent. */
export interface AgentEarningWriteOff {
  id: number;
  spid: number;
  amountUsd: number;
  reversalEarningId: number;
  offsetEarningId: number;
  reason: string;
  actor: string;
  createdAt: string;
}

/** 200 from the shortfall write-off: the new records plus what the write-off could not cover. */
export interface WriteOffShortfallResult {
  /** The write-off records just created, newest first. */
  writeOffs: AgentEarningWriteOff[];
  writtenOffUsd: number;
  /** Negative balance still left after the write-off; 0 when the balance reached 0. */
  uncoveredUsd: number;
}

/** GET /api/agents/admin/{spid} — full admin detail for one agent. */
export interface AgentAdminDetail {
  profile: AgentAdminRow & { referredByAgentSpid: number | null };
  earnings: {
    totals: { lockedUsd: number; availableUsd: number; redeemedUsd: number };
    entries: AgentEarningsEntry[];
  };
  history: AgentAuditEntry[];
  /** Referral code of the recruiting agent, when referredByAgentSpid is set. */
  referredByCode: string | null;
  /** Direct recruits (umbrella downline) with per-recruit earnings attribution. */
  downline: AgentDownlineEntry[];
  /** Recruiting invites this agent has issued. */
  invites: AgentInviteEntry[];
  /** Shortfall write-offs, newest first; empty when none. */
  writeOffs: AgentEarningWriteOff[];
}

/** One held earnings row from GET /api/agents/admin/earnings/held (newest first, max 200). */
export interface HeldEarning {
  earningId: number;
  spid: number;
  agentName: string;
  earningType: AgentEarningType;
  amountUsd: number;
  capTripped: string | null;
  createdAt: string;
}

export type OverrideCascadeOutcome = 'None' | 'Reversed' | 'AlreadyReversed' | 'Failed';

export interface ReverseHeldEarningResult {
  earningId: number;
  reversalId: number;
  overrideCascade: OverrideCascadeOutcome;
  /** Set only when overrideCascade is Failed: reverse the same earning again to retry. */
  warning: string | null;
}

export type OverrideReleaseOutcome = 'None' | 'Released' | 'Failed';

export interface ReleaseHeldEarningResult {
  earningId: number;
  status: string | number;
  overrideRelease: OverrideReleaseOutcome;
  /** Set only when overrideRelease is Failed. */
  warning: string | null;
}

export interface RerunAllResult {
  rowsAdded: number;
  rowsSkipped: number;
}

export interface ClearLinkResult {
  agentSpid: number;
  cleared: boolean;
}

/** Machine code the API sends with the 409 that refuses a link clear inside the clawback window. */
export const CLEAR_INSIDE_CLAWBACK_WINDOW = 'clear-inside-clawback-window';

/** A refused request that carried a machine `code` (and, for the clawback refusal, the earliest clear date). */
export class AgentAdminApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** ISO-8601 with offset; null when the API could not compute it. */
  readonly earliestClearAllowedAt: string | null;

  constructor(
    message: string,
    status: number,
    code: string | null,
    earliestClearAllowedAt: string | null
  ) {
    super(message);
    this.name = 'AgentAdminApiError';
    this.status = status;
    this.code = code;
    this.earliestClearAllowedAt = earliestClearAllowedAt;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function callApi<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const message =
      (body['message'] as string | undefined) ??
      (body['error'] as string | undefined) ??
      `HTTP ${response.status}: ${response.statusText}`;
    if (response.status === 409 && body['code'] === CLEAR_INSIDE_CLAWBACK_WINDOW) {
      const earliest = body['earliestClearAllowedAt'];
      throw new AgentAdminApiError(
        message,
        response.status,
        CLEAR_INSIDE_CLAWBACK_WINDOW,
        typeof earliest === 'string' ? earliest : null
      );
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

/**
 * POST an admin action; sends `{ reason }` as the JSON body when a non-empty
 * reason is provided, otherwise no body (the API accepts an empty body).
 */
function postAction(spid: number, action: string, reason?: string): Promise<AgentAdminRow> {
  const trimmed = reason?.trim();
  return callApi<AgentAdminRow>(`${AGENTS_ADMIN}/${spid}/${action}`, {
    method: 'POST',
    ...(trimmed ? { body: JSON.stringify({ reason: trimmed }) } : {}),
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * List agent applications by approval status (default Pending).
 * GET /api/agents/admin/pending?status={status}
 */
export async function getAgents(
  status: AgentApprovalStatus = 'Pending'
): Promise<AgentAdminRow[]> {
  return callApi<AgentAdminRow[]>(`${AGENTS_ADMIN}/pending?status=${status}`);
}

/**
 * Full admin detail for one agent — profile, earnings totals + ledger,
 * lifecycle audit history, recruit attribution.
 * GET /api/agents/admin/{spid}
 */
export async function getAgentDetail(spid: number): Promise<AgentAdminDetail> {
  const detail = await callApi<AgentAdminDetail>(`${AGENTS_ADMIN}/${spid}`);
  return {
    ...detail,
    writeOffs: detail.writeOffs ?? [],
    earnings: {
      ...detail.earnings,
      entries: (detail.earnings?.entries ?? []).map((entry) => ({
        ...entry,
        hasStandingOverride: entry.hasStandingOverride ?? false,
      })),
    },
  };
}

/**
 * Approve a pending (or reinstate a suspended/rejected) agent. Flips ALL of
 * the agent's Locked earnings to Available in the same transaction (FR-103 /
 * acceptance scenario 2).
 * POST /api/agents/admin/{spid}/approve
 */
export async function approveAgent(spid: number, reason?: string): Promise<AgentAdminRow> {
  return postAction(spid, 'approve', reason);
}

/**
 * Reject a pending application — accrued earnings stay Locked.
 * POST /api/agents/admin/{spid}/reject
 */
export async function rejectAgent(spid: number, reason?: string): Promise<AgentAdminRow> {
  return postAction(spid, 'reject', reason);
}

/**
 * Suspend an approved agent — deactivates their referral code immediately.
 * POST /api/agents/admin/{spid}/suspend
 */
export async function suspendAgent(spid: number, reason?: string): Promise<AgentAdminRow> {
  return postAction(spid, 'suspend', reason);
}

/**
 * Earnings currently held by a cap, across all approved agents.
 * GET /api/agents/admin/earnings/held
 */
export async function getHeldEarnings(): Promise<HeldEarning[]> {
  return callApi<HeldEarning[]>(`${AGENTS_ADMIN}/earnings/held`);
}

/** One recruiter override still standing (or held) after its parent earning moved on. */
export interface StandingOverrideItem {
  overrideEarningId: number;
  parentEarningId: number;
  /** The recruiter holding the override; the id getAgentDetail takes. */
  agentSpid: number;
  agentName: string | null;
  /** ReversedParent: parent reversed, override still standing. ReleasedParent: parent released, override still held. */
  kind: 'ReversedParent' | 'ReleasedParent';
  sinceUtc: string;
  spent: boolean;
  amountUsd: number;
}

export interface StandingOverrideSummary {
  standingCount: number;
  spentCount: number;
  oldestSinceUtc: string | null;
  items: StandingOverrideItem[];
}

/**
 * Cross-agent summary of recruiter overrides left standing after a parent reversal/release.
 * GET /api/agents/admin/overrides/standing
 */
export async function getStandingOverrideSummary(): Promise<StandingOverrideSummary> {
  const raw = await callApi<Partial<StandingOverrideSummary>>(`${AGENTS_ADMIN}/overrides/standing`);
  return {
    standingCount: raw.standingCount ?? 0,
    spentCount: raw.spentCount ?? 0,
    oldestSinceUtc: raw.oldestSinceUtc ?? null,
    items: raw.items ?? [],
  };
}

function postReason<T>(url: string, reason: string): Promise<T> {
  return callApi<T>(url, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
}

/**
 * Release a held earning. A reason is required (the API answers 400 when blank).
 * POST /api/agents/admin/earnings/{earningId}/release
 */
export async function releaseHeldEarning(
  earningId: number,
  reason: string
): Promise<ReleaseHeldEarningResult> {
  const raw = await postReason<
    Omit<ReleaseHeldEarningResult, 'overrideRelease' | 'warning'> &
      Partial<Pick<ReleaseHeldEarningResult, 'overrideRelease' | 'warning'>>
  >(`${AGENTS_ADMIN}/earnings/${earningId}/release`, reason);
  // An older server omits the override fields; absent means no override moved.
  return { ...raw, overrideRelease: raw.overrideRelease ?? 'None', warning: raw.warning ?? null };
}

/**
 * Reverse a held earning. A reason is required.
 * POST /api/agents/admin/earnings/{earningId}/reverse
 */
export async function reverseHeldEarning(
  earningId: number,
  reason: string
): Promise<ReverseHeldEarningResult> {
  const raw = await postReason<
    Omit<ReverseHeldEarningResult, 'overrideCascade' | 'warning'> &
      Partial<Pick<ReverseHeldEarningResult, 'overrideCascade' | 'warning'>>
  >(`${AGENTS_ADMIN}/earnings/${earningId}/reverse`, reason);
  // An older server omits the cascade fields; absent means no cascade ran.
  return { ...raw, overrideCascade: raw.overrideCascade ?? 'None', warning: raw.warning ?? null };
}

/**
 * Re-run accrual for one agent, recovering dropped rows. No body; answers 503
 * `{ error }` when the milestone source is unavailable.
 * POST /api/agents/admin/{spid}/earnings/rerun-all
 */
export async function rerunAllEarnings(spid: number): Promise<RerunAllResult> {
  return callApi<RerunAllResult>(`${AGENTS_ADMIN}/${spid}/earnings/rerun-all`, {
    method: 'POST',
  });
}

/**
 * Clear the agent's linked Chelless account. A reason is required. A 409 with code
 * 'clear-inside-clawback-window' rejects with an AgentAdminApiError.
 * POST /api/agents/admin/{spid}/link/clear
 */
export async function clearAgentLink(spid: number, reason: string): Promise<ClearLinkResult> {
  return postReason(`${AGENTS_ADMIN}/${spid}/link/clear`, reason);
}

/**
 * Write off the agent's unrecovered clawback shortfall. A reason (1-400 chars)
 * is required. A 409 rejects with Error.message set to the API `error` code:
 * 'no-negative-balance' (balance is not negative), 'shortfall-not-coverable'
 * (balance is negative but no reversal debt is left to write off; nothing was
 * written) or 'write-off-conflict'.
 * POST /api/agents/admin/{spid}/earnings/write-off
 */
export async function writeOffShortfall(
  spid: number,
  reason: string
): Promise<WriteOffShortfallResult> {
  return postReason(`${AGENTS_ADMIN}/${spid}/earnings/write-off`, reason);
}

export const agentAdminService = {
  getAgents,
  getAgentDetail,
  approveAgent,
  rejectAgent,
  suspendAgent,
  getHeldEarnings,
  getStandingOverrideSummary,
  releaseHeldEarning,
  reverseHeldEarning,
  rerunAllEarnings,
  clearAgentLink,
  writeOffShortfall,
};
